/**
 * Rotulos, tons e icones da Central.
 *
 * Records com chave de uniao literal (e nao index signature): se o contrato
 * ganhar um estado, o tsc aponta o Record incompleto em vez de a tela
 * renderizar `undefined`. Cor de status nunca viaja sozinha: todo consumidor
 * mostra o rotulo escrito junto.
 */

import type { BadgeTone, IconName } from '../../components/ui';
import type { BillingModel, CompanyStatus, FunnelKind } from '../../types';
import type { AlertKind, AlertSeverity, CostChannel, PlatformEventKind, WhatsAppRoute } from './types';

export const COMPANY_STATUS_LABEL: Readonly<Record<CompanyStatus, string>> = {
  ativa: 'Ativa',
  onboarding: 'Em implantação',
  pausada: 'Pausada',
};

export const COMPANY_STATUS_TONE: Readonly<Record<CompanyStatus, BadgeTone>> = {
  ativa: 'good',
  onboarding: 'info',
  pausada: 'neutral',
};

/** Ordem de exibicao dos status: do que gera receita para o que nao gera. */
export const COMPANY_STATUS_ORDER: readonly CompanyStatus[] = ['ativa', 'onboarding', 'pausada'];

/** Cor da fatia do status na barra empilhada do contador; o texto vai sempre ao lado. */
export const COMPANY_STATUS_BAR: Readonly<Record<CompanyStatus, string>> = {
  ativa: 'bg-status-good',
  onboarding: 'bg-info',
  pausada: 'bg-ink-subtle',
};

/** "A definir" fica em warning de proposito: o dono ainda nao decidiu o modelo (24/09/2026). */
export const BILLING_LABEL: Readonly<Record<BillingModel, string>> = {
  contrato: 'Contrato',
  automatico: 'Automático',
  a_definir: 'A definir',
};

export const BILLING_TONE: Readonly<Record<BillingModel, BadgeTone>> = {
  contrato: 'brand',
  automatico: 'info',
  a_definir: 'warning',
};

export const FUNNEL_LABEL: Readonly<Record<FunnelKind, string>> = {
  carrinho_abandonado: 'Carrinho abandonado',
  expirados: 'Expirados',
};

/** Mesmos glifos do menu lateral (routes.tsx), para o funil ter uma cara so. */
export const FUNNEL_ICON: Readonly<Record<FunnelKind, IconName>> = {
  carrinho_abandonado: 'carrinho',
  expirados: 'calendario',
};

export const ALERT_SEVERITY_LABEL: Readonly<Record<AlertSeverity, string>> = {
  critical: 'Crítico',
  warning: 'Atenção',
  info: 'Aviso',
};

export const ALERT_SEVERITY_TONE: Readonly<Record<AlertSeverity, BadgeTone>> = {
  critical: 'critical',
  warning: 'warning',
  info: 'info',
};

/** Fundo e cor do glifo do alerta, por gravidade. */
export const ALERT_SEVERITY_CHIP: Readonly<Record<AlertSeverity, string>> = {
  critical: 'bg-status-critical/10 text-status-critical',
  warning: 'bg-status-warning/10 text-status-warning',
  info: 'bg-info/10 text-info',
};

export const ALERT_KIND_LABEL: Readonly<Record<AlertKind, string>> = {
  integracao_sem_evento: 'Integração sem evento há 3 dias',
  whatsapp_desconectado: 'WhatsApp desconectado',
  chargeback_recebido: 'Chargeback recebido',
  implantacao_sem_webhook: 'Em implantação sem webhook',
  margem_negativa: 'Margem negativa no período',
};

export const ALERT_KIND_ICON: Readonly<Record<AlertKind, IconName>> = {
  integracao_sem_evento: 'link',
  whatsapp_desconectado: 'whatsapp',
  chargeback_recebido: 'alerta',
  implantacao_sem_webhook: 'chave',
  margem_negativa: 'moeda',
};

/** O que o botao de acao do alerta faz. Cada tipo tem UMA acao, a que resolve. */
export type AlertAction = 'cadastro' | 'comissoes' | 'custos';

export const ALERT_KIND_ACTION: Readonly<Record<AlertKind, { readonly kind: AlertAction; readonly label: string }>> = {
  integracao_sem_evento: { kind: 'cadastro', label: 'Conferir integração' },
  whatsapp_desconectado: { kind: 'cadastro', label: 'Reconectar no cadastro' },
  chargeback_recebido: { kind: 'comissoes', label: 'Ver comissões' },
  implantacao_sem_webhook: { kind: 'cadastro', label: 'Abrir cadastro' },
  margem_negativa: { kind: 'custos', label: 'Detalhar custos' },
};

export const EVENT_KIND_LABEL: Readonly<Record<PlatformEventKind, string>> = {
  carrinho_abandonado: 'Carrinho abandonado',
  ligacao_ia: 'Ligação da IA',
  whatsapp_enviado: 'WhatsApp enviado',
  email_enviado: 'E-mail enviado',
  venda_recuperada: 'Venda recuperada',
  estorno: 'Estorno recebido',
  webhook_recebido: 'Webhook recebido',
  empresa_cadastrada: 'Empresa cadastrada',
};

export const EVENT_KIND_ICON: Readonly<Record<PlatformEventKind, IconName>> = {
  carrinho_abandonado: 'carrinho',
  ligacao_ia: 'telefone',
  whatsapp_enviado: 'whatsapp',
  email_enviado: 'email',
  venda_recuperada: 'moeda',
  estorno: 'alerta',
  webhook_recebido: 'link',
  empresa_cadastrada: 'empresa',
};

/**
 * Fundo e cor do glifo do evento. Voz = serie 1, WhatsApp = serie 3 e e-mail
 * = serie 2 seguem a convencao que ja existe no painel (visao geral e
 * Contatos); venda e estorno usam status, o resto e neutro.
 */
export const EVENT_KIND_CHIP: Readonly<Record<PlatformEventKind, string>> = {
  carrinho_abandonado: 'bg-ink/5 text-ink-muted',
  ligacao_ia: 'bg-series-1/10 text-series-1',
  whatsapp_enviado: 'bg-series-3/10 text-series-3',
  email_enviado: 'bg-series-2/10 text-series-2',
  venda_recuperada: 'bg-status-good/10 text-status-good',
  estorno: 'bg-status-critical/10 text-status-critical',
  webhook_recebido: 'bg-info/10 text-info',
  empresa_cadastrada: 'bg-brand/10 text-brand',
};

// -----------------------------------------------------------------------------
// Custos por canal
// -----------------------------------------------------------------------------

/** Ordem de leitura dos canais de custo: do mais caro (voz) ao rateado (infra). */
export const COST_CHANNEL_ORDER: readonly CostChannel[] = ['voice', 'whatsapp', 'email', 'infra'];

export const COST_CHANNEL_LABEL: Readonly<Record<CostChannel, string>> = {
  voice: 'Voz (Vapi)',
  whatsapp: 'WhatsApp',
  email: 'E-mail',
  infra: 'Infra (servidor e banco)',
};

export const COST_CHANNEL_SHORT: Readonly<Record<CostChannel, string>> = {
  voice: 'Voz',
  whatsapp: 'WhatsApp',
  email: 'E-mail',
  infra: 'Infra',
};

export const COST_CHANNEL_ICON: Readonly<Record<CostChannel, IconName>> = {
  voice: 'telefone',
  whatsapp: 'whatsapp',
  email: 'email',
  infra: 'escudo',
};

/**
 * Slot da paleta de series por canal. Voz, e-mail e WhatsApp repetem os
 * slots dos eventos (1, 2, 3) para a cor significar a mesma coisa em toda a
 * pagina; infra fica com o 4, que nenhum canal de contato usa.
 */
export const COST_CHANNEL_TONE: Readonly<Record<CostChannel, 1 | 2 | 3 | 4>> = {
  voice: 1,
  email: 2,
  whatsapp: 3,
  infra: 4,
};

export const COST_CHANNEL_DOT: Readonly<Record<CostChannel, string>> = {
  voice: 'bg-series-1',
  email: 'bg-series-2',
  whatsapp: 'bg-series-3',
  infra: 'bg-series-4',
};

export const WHATSAPP_ROUTE_LABEL: Readonly<Record<WhatsAppRoute, string>> = {
  meta_oficial: 'API oficial da Meta',
  evolution: 'Evolution API',
};
